import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { X, MapPin, Car, Clock, Loader2 } from "lucide-react";

const DURATION_OPTIONS = [
  { label: "Leaving soon", value: "leaving_soon" },
  { label: "30 min", value: "30min" },
  { label: "1 hour", value: "1hr" },
  { label: "2 hours", value: "2hrs" },
  { label: "Not sure", value: "unknown" },
];

export default function SpotSubmissionSheet({ businessId, businessName, onClose, onSuccess }) {
  const [spotType, setSpotType] = useState(null);
  const [duration, setDuration] = useState("unknown");
  const [note, setNote] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gettingGps, setGettingGps] = useState(false);
  const [coords, setCoords] = useState(null);
  const [adjacent, setAdjacent] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState(businessId);

  // Get GPS on mount
  useEffect(() => {
    if (navigator.geolocation) {
      setGettingGps(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setGettingGps(false);
        },
        () => { setGettingGps(false); toast.error("Location access required to spot a truck"); },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  // Check adjacent vendors after coords + spotType
  useEffect(() => {
    if (!coords || !spotType) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/trucks/check-adjacent/${businessId}?lat=${coords.lat}&lng=${coords.lng}`);
        setAdjacent(res.data?.adjacent_trucks || []);
      } catch { /* ignore */ }
    })();
  }, [coords, spotType, businessId]);

  const handleSubmit = async () => {
    if (!spotType) { toast.error("Select how you spotted the truck"); return; }
    if (!coords) { toast.error("Waiting for GPS..."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/trucks/spot`, {
        business_id: selectedBiz,
        lat: coords.lat,
        lng: coords.lng,
        spot_type: spotType,
        estimated_still_here: duration,
        anonymous,
      });
      if (navigator.vibrate) navigator.vibrate(50);
      toast.success("Spot reported. Thanks for helping your community!");
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit spot");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="spot-submission-sheet">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Sheet */}
      <div className="relative w-full max-w-md bg-white rounded-t-2xl px-5 pt-4 pb-8 animate-slide-up" style={{ animation: "slideUp 0.3s ease-out" }}>
        {/* Handle */}
        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4" />

        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center" data-testid="spot-close-btn">
          <X className="w-4 h-4 text-slate-400" />
        </button>

        <h2 className="text-base font-black text-slate-800 mb-1" data-testid="spot-title">Spot this truck</h2>
        <p className="text-xs text-slate-400 mb-4">{businessName}</p>

        {gettingGps && (
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
            <Loader2 className="w-3 h-3 animate-spin" /> Getting your location...
          </div>
        )}

        {/* Adjacent vendor disambiguation */}
        {adjacent.length > 0 && (
          <div className="mb-4 p-3 bg-amber-50 rounded-xl border border-amber-200" data-testid="spot-disambiguation">
            <p className="text-xs font-bold text-amber-700 mb-2">Which truck are you spotting?</p>
            <div className="space-y-2">
              <DisambigButton
                name={businessName}
                selected={selectedBiz === businessId}
                onClick={() => setSelectedBiz(businessId)}
                testId="disambig-original"
              />
              {adjacent.map(t => (
                <DisambigButton
                  key={t.business_id}
                  name={t.name}
                  photo={t.photo}
                  selected={selectedBiz === t.business_id}
                  onClick={() => setSelectedBiz(t.business_id)}
                  testId={`disambig-${t.business_id}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Spot type */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button
            onClick={() => setSpotType("on_foot")}
            className={`flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 transition ${
              spotType === "on_foot" ? "border-[#F59E0B] bg-amber-50" : "border-slate-200 bg-white"
            }`}
            data-testid="spot-type-on-foot"
          >
            <MapPin className={`w-5 h-5 ${spotType === "on_foot" ? "text-[#F59E0B]" : "text-slate-400"}`} />
            <span className="text-xs font-bold text-slate-700">I am here</span>
          </button>
          <button
            onClick={() => setSpotType("drove_past")}
            className={`flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 transition ${
              spotType === "drove_past" ? "border-[#F59E0B] bg-amber-50" : "border-slate-200 bg-white"
            }`}
            data-testid="spot-type-drove-past"
          >
            <Car className={`w-5 h-5 ${spotType === "drove_past" ? "text-[#F59E0B]" : "text-slate-400"}`} />
            <span className="text-xs font-bold text-slate-700">I drove past</span>
          </button>
        </div>

        {/* Duration */}
        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2">How long are they here?</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {DURATION_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setDuration(opt.value)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition ${
                duration === opt.value ? "bg-[#F59E0B] text-white" : "bg-slate-100 text-slate-600"
              }`}
              data-testid={`duration-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Note */}
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 60))}
          placeholder="Any details? Line or wait time"
          className="w-full px-3 py-2.5 rounded-xl bg-slate-50 text-xs text-slate-700 placeholder:text-slate-300 border border-slate-200 mb-3"
          data-testid="spot-note-input"
        />

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading || !spotType || !coords}
          className="w-full py-3 rounded-xl bg-[#F59E0B] text-white text-sm font-bold hover:bg-[#D97706] transition disabled:opacity-50"
          data-testid="spot-submit-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Submit Spot"}
        </button>

        {/* Anonymous toggle */}
        <button
          onClick={() => setAnonymous(!anonymous)}
          className="w-full text-center mt-2"
          data-testid="spot-anonymous-toggle"
        >
          <span className="text-[10px] text-slate-400">
            Your first name will be shown with this spot.{" "}
            <span className="text-[#F59E0B] font-medium">
              {anonymous ? "Show my name instead" : "Tap to report anonymously"}
            </span>
          </span>
        </button>
      </div>

      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
    </div>
  );
}

function DisambigButton({ name, photo, selected, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 p-2 rounded-lg border-2 transition ${
        selected ? "border-[#F59E0B] bg-amber-50" : "border-slate-200"
      }`}
      data-testid={testId}
    >
      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center overflow-hidden shrink-0">
        {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> :
          <span className="text-sm">{"\uD83D\uDE9A"}</span>}
      </div>
      <span className="text-xs font-bold text-slate-700">{name}</span>
    </button>
  );
}
